/**
 * The one correct pointer-drag contract (Mobile P17).
 *
 * Every hand-rolled drag in this repo except `reorder-preview.js` is missing at
 * least one of the exits below, and each omission is a bug that exists today:
 * a drag that never ends when the OS takes the pointer away, a `document.body`
 * cursor blanked instead of restored, an undo transaction opened by a gesture
 * that turned out to be a scroll. This module is the single implementation, so
 * fixing one of those fixes all of them.
 *
 * It lives in `shared/` for two reasons: `ui-consistency-sweep.js` sweeps only
 * `['ui','editing']`, so a drag helper in `editing/` would trip its own rules;
 * and `shared/` must not depend on `core/`, which is why the undo transaction
 * arrives as injected callbacks rather than an import (the `collab-guard.js`
 * injectable pattern is the precedent).
 *
 * `reorder-preview.js` deliberately does NOT adopt this (decision D8): it is a
 * template literal embedded in the sandboxed-iframe bridge, with no module
 * loader and no debugger, and it is the only drag in the tree that already gets
 * this right. `POINTER_DRAG_CONTRACT` below is the fence against it drifting.
 */

import {
  ACTIVATION_PX,
  SCROLL_RELEASE_PX,
  TOUCH_LONG_PRESS_MS,
  exceedsActivation,
  exceedsScrollRelease
} from './touch-geometry.js';

/**
 * The hooks a correct drag must wire. `touch-reorder-tests.js` asserts the
 * generated bridge string still contains each of these, so the one drag that is
 * exempt from this module cannot quietly lose an exit either.
 */
export const POINTER_DRAG_CONTRACT = Object.freeze([
  'pointerdown',
  'pointermove',
  'pointerup',
  'pointercancel',
  'lostpointercapture',
  'blur',
  'setPointerCapture'
]);

/** Exit reasons passed to `onCancel`/`onEnd`. `up` is the only success. */
export const DRAG_EXIT = Object.freeze({
  UP: 'up',
  CANCEL: 'pointercancel',
  LOST_CAPTURE: 'lostpointercapture',
  BLUR: 'blur',
  ESCAPE: 'escape',
  DESTROY: 'destroy'
});

const isMouseLike = (pointerType) => !pointerType || pointerType === 'mouse';

/**
 * Begin a drag from a pointerdown that has ALREADY fired.
 *
 * This is the engine. `createPointerDrag` below is the target-bound wrapper;
 * callers that own their own pointerdown (value-scrubber, which is entered from
 * four different widgets' handlers) use this directly.
 *
 * @param {PointerEvent} event  the live pointerdown
 * @param {object} opts         see createPointerDrag
 * @returns {{cancel: (reason?: string) => void, end: () => void, isArmed: () => boolean}}
 */
export function startPointerDrag(event, opts = {}) {
  const {
    touchLongPressMs = TOUCH_LONG_PRESS_MS,
    scrollReleasePx = SCROLL_RELEASE_PX,
    activationPx = ACTIVATION_PX,
    touchAction = 'none',
    capture = true,
    cursor,
    userSelect = true,
    bodyClass,
    overlayEl,
    overlayClass,
    transaction,
    escapeCancels = true,
    suppressClickMs = 0,
    onArm,
    onStart,
    onMove,
    onEnd,
    onCancel
  } = opts;

  const doc = event?.target?.ownerDocument || globalThis.document;
  const win = doc?.defaultView || globalThis.window;
  if (!doc || !win) return { cancel() {}, end() {}, isArmed: () => false };

  const pointerId = event.pointerId;
  const startX = event.clientX;
  const startY = event.clientY;
  const captureTarget = event.currentTarget || event.target;

  const touchLike = !isMouseLike(event.pointerType);

  // Starts false for BOTH pointer kinds. A mouse arms synchronously at the end
  // of this function and a finger arms on the long-press timer, but both go
  // through arm() — pre-setting this true for a mouse made arm() early-return,
  // so the mouse path silently never called setPointerCapture (guarantee 1/2).
  let armed = false;
  let started = false; // onStart has fired — the drag is real
  let finished = false; // the funnel has run (guarantee 4)
  let longPressTimer = 0;
  let transactionOpen = false;

  // Restore TARGETS, captured before anything is changed. Guarantee 4 is that
  // these are restored to what they were, not blanked to ''. Four call sites in
  // this repo hard-blank them today, which stomps any outer drag that had set
  // its own cursor — a nested-drag bug you only see on the second gesture.
  const body = doc.body;
  const prevCursor = body ? body.style.cursor : '';
  const prevUserSelect = body ? body.style.userSelect : '';
  const prevTouchAction = captureTarget?.style ? captureTarget.style.touchAction : '';

  const clearLongPress = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = 0;
    }
  };

  /** Guarantee 2: capture the pointer and suppress native scroll, once armed. */
  const arm = () => {
    if (armed) return;
    armed = true;
    clearLongPress();
    if (capture && captureTarget?.setPointerCapture) {
      try {
        captureTarget.setPointerCapture(pointerId);
      } catch {
        // Capture is a best-effort optimisation: the element may already be
        // detached, or the pointer already released. The drag still works off
        // the document-level listeners below.
      }
    }
    if (captureTarget?.style && touchAction) captureTarget.style.touchAction = touchAction;
    if (typeof onArm === 'function') onArm({ event });
  };

  /** Fires once, the first time movement clears `activationPx`. */
  const begin = (e) => {
    if (started) return;
    started = true;
    if (body) {
      if (cursor) body.style.cursor = cursor;
      if (userSelect) body.style.userSelect = 'none';
      if (bodyClass) body.classList.add(bodyClass);
    }
    if (overlayEl && overlayClass) overlayEl.classList.add(overlayClass);
    // Guarantee 5: begin() here and NOT at pointerdown. A gesture that turns
    // out to be a scroll releases before this line, so it must not have opened
    // an undo transaction that nothing will ever commit.
    if (transaction && typeof transaction.begin === 'function') {
      transaction.begin();
      transactionOpen = true;
    }
    if (typeof onStart === 'function') onStart({ event: e, startX, startY });
  };

  /**
   * Guarantee 4: ONE idempotent funnel that always runs. Every exit — a clean
   * release, a cancelled pointer, a lost capture, a blurred window, Escape, or
   * destroy() — lands here, so there is no path that leaves the body cursor
   * set, an overlay class stuck on, or a transaction open.
   */
  const finish = (reason, e) => {
    if (finished) return;
    finished = true;
    clearLongPress();
    detach();

    if (
      capture &&
      captureTarget?.releasePointerCapture &&
      captureTarget.hasPointerCapture?.(pointerId)
    ) {
      try {
        captureTarget.releasePointerCapture(pointerId);
      } catch {
        // Already released by the UA on cancel — nothing to undo.
      }
    }
    if (captureTarget?.style) captureTarget.style.touchAction = prevTouchAction;

    if (body) {
      body.style.cursor = prevCursor;
      body.style.userSelect = prevUserSelect;
      if (bodyClass) body.classList.remove(bodyClass);
    }
    // Guarantee 6: on EVERY cancel path, not just the clean one. In
    // glass-spacing this class is what flips the whole overlay root to
    // pointer-events:auto — leaving it on means the app silently swallows every
    // click with no visible cause and no recovery short of a reload.
    if (overlayEl && overlayClass) overlayEl.classList.remove(overlayClass);

    if (transactionOpen && transaction && typeof transaction.commit === 'function') {
      transactionOpen = false;
      transaction.commit();
    }

    // Guarantee 8: a touch drag ends with a synthesized click on whatever is
    // under the finger. Swallow exactly one, and only for a real drag.
    if (suppressClickMs > 0 && started && touchLike) {
      const swallow = (clickEvent) => {
        clickEvent.stopPropagation();
        clickEvent.preventDefault();
      };
      doc.addEventListener('click', swallow, true);
      setTimeout(() => doc.removeEventListener('click', swallow, true), suppressClickMs);
    }

    if (reason === DRAG_EXIT.UP) {
      if (started && typeof onEnd === 'function') onEnd({ event: e, reason });
    } else if (started && typeof onCancel === 'function') {
      onCancel({ event: e, reason });
    }
  };

  function onPointerMove(e) {
    if (finished || e.pointerId !== pointerId) return;

    // Guarantee 1, and the single most important property here: until a finger
    // has armed, a move past the scroll threshold means the user is SCROLLING.
    // Release with no preventDefault so the page scrolls natively. Getting this
    // wrong makes the whole surface feel broken, not merely wrong.
    if (!armed) {
      if (exceedsScrollRelease(e.clientX - startX, e.clientY - startY, scrollReleasePx)) {
        finish(DRAG_EXIT.CANCEL, e);
      }
      return;
    }

    if (!started) {
      if (!exceedsActivation(e.clientX - startX, e.clientY - startY, activationPx)) return;
      begin(e);
    }
    if (typeof onMove === 'function') {
      onMove({ event: e, dx: e.clientX - startX, dy: e.clientY - startY, startX, startY });
    }
  }

  function onPointerUp(e) {
    if (finished || e.pointerId !== pointerId) return;
    finish(DRAG_EXIT.UP, e);
  }

  function onPointerCancel(e) {
    if (finished || e.pointerId !== pointerId) return;
    finish(DRAG_EXIT.CANCEL, e);
  }

  function onLostCapture(e) {
    if (finished || e.pointerId !== pointerId) return;
    finish(DRAG_EXIT.LOST_CAPTURE, e);
  }

  function onBlur() {
    if (finished) return;
    finish(DRAG_EXIT.BLUR);
  }

  function onKeyDown(e) {
    if (finished || !escapeCancels || e.key !== 'Escape') return;
    finish(DRAG_EXIT.ESCAPE, e);
  }

  function detach() {
    doc.removeEventListener('pointermove', onPointerMove, true);
    doc.removeEventListener('pointerup', onPointerUp, true);
    doc.removeEventListener('pointercancel', onPointerCancel, true);
    doc.removeEventListener('lostpointercapture', onLostCapture, true);
    doc.removeEventListener('keydown', onKeyDown, true);
    win.removeEventListener('blur', onBlur);
  }

  // Guarantee 3: the full set. pointercancel, lostpointercapture and blur are
  // the three every hand-rolled drag here omits, and their absence is what
  // strands a drag when the OS takes the pointer — an incoming call, an app
  // switch, a system gesture.
  doc.addEventListener('pointermove', onPointerMove, true);
  doc.addEventListener('pointerup', onPointerUp, true);
  doc.addEventListener('pointercancel', onPointerCancel, true);
  doc.addEventListener('lostpointercapture', onLostCapture, true);
  doc.addEventListener('keydown', onKeyDown, true);
  win.addEventListener('blur', onBlur);

  if (touchLike) {
    longPressTimer = setTimeout(arm, touchLongPressMs);
  } else {
    arm();
  }

  return {
    cancel(reason = DRAG_EXIT.DESTROY) {
      finish(reason);
    },
    end() {
      finish(DRAG_EXIT.UP);
    },
    isArmed: () => armed
  };
}

/**
 * Install a drag on `target`. Returns `{destroy}`.
 *
 * @param {object} opts
 * @param {Element} opts.target            element whose pointerdown starts a drag
 * @param {(e: PointerEvent) => boolean} [opts.filter]  return false to ignore this pointerdown
 * @param {number} [opts.touchLongPressMs=250]  how long a finger must rest before arming
 * @param {number} [opts.scrollReleasePx=10]    pre-arm travel that hands the gesture back to the scroller
 * @param {number} [opts.activationPx=5]        travel before onStart fires
 * @param {string} [opts.touchAction='none']    set on the target while armed, restored on exit
 * @param {boolean} [opts.capture=true]         setPointerCapture on arm
 * @param {string} [opts.cursor]                document.body cursor while dragging
 * @param {boolean} [opts.userSelect=true]      suppress selection while dragging
 * @param {string} [opts.bodyClass]             class on document.body while dragging
 * @param {Element} [opts.overlayEl]            element carrying opts.overlayClass
 * @param {string} [opts.overlayClass]          class added on start, removed on EVERY exit
 * @param {{begin: Function, commit: Function}} [opts.transaction]  undo batching, injected
 * @param {boolean} [opts.escapeCancels=true]
 * @param {number} [opts.suppressClickMs=0]     swallow the synthesized click after a touch drag
 * @param {Function} [opts.onArm] @param {Function} [opts.onStart] @param {Function} [opts.onMove]
 * @param {Function} [opts.onEnd] @param {Function} [opts.onCancel]
 * @returns {{destroy: () => void}}
 */
export function createPointerDrag(opts = {}) {
  const { target, filter } = opts;
  if (!target?.addEventListener) return { destroy() {} };

  let live = null;

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    if (typeof filter === 'function' && filter(e) === false) return;
    // A second pointerdown while a drag is live cancels the first rather than
    // running two — two live drags share one document listener set and would
    // detach each other's.
    live?.cancel(DRAG_EXIT.CANCEL);
    live = startPointerDrag(e, opts);
  };

  target.addEventListener('pointerdown', onPointerDown);

  return {
    // Guarantee 7: tearing down mid-drag runs the cancel path, so a component
    // unmounted during a gesture cannot leave the body cursor or an overlay
    // class behind it.
    destroy() {
      target.removeEventListener('pointerdown', onPointerDown);
      live?.cancel(DRAG_EXIT.DESTROY);
      live = null;
    }
  };
}
